// src/pages/merchant-pages/AddNoCustomerContainerModal.js
import { useState, useContext } from "react";
import { useTranslation } from "react-i18next";

// Assets

// Utils
import { DataContext } from "../../utils/DataContext";
import { onlyNums } from "../../utils/OnlyNums";
import { formatDate } from "../../utils/toDate";
import { shouldCloseModal } from "../../utils/shouldCloseModal";

// Styles
import classes from "../../components/Modal.module.css";

// Components
import ErrorBox from "../../components/ErrorBox";
import Loader from "../../components/Loader";
import CustomInput from "../../components/CustomInput";
import Dropdown from "../../components/Dropdown";
import CustomButton from "../../components/CustomButton";
import Modal from "../../components/Modal";
import HotDropdown from "../../components/HotDropdown";
import AreYouSureModal from "../../components/AreYouSureModal";

const AddNoCustomerContainerModal = ({
  inputs,
  setInputs,
  setShowAddShippedContainerModal,
  onClose,
}) => {
  const { t } = useTranslation("general");

  const { selectedSupplier, error, setError } = useContext(DataContext);

  const [formData, setFormData] = useState({
    containerNumber: "",
    expectedArrivalDate: "",
    finalDestination: "",
    shipper: "",
    material: "",
    numberOfBoxes: 0,
    measuringUnit: "",
    unitPrice: 0,
    exitDate: "",
    dateOfEntry: new Date(),
    shippingDate: "",
    permitsReceivingDate: "",
    amountForSupplier: "",
    abstractAmountForCustomer: "",
    finalAmountForCustomer: "",
    notes: "",
  });

  const [delivered, setDelivered] = useState({
    title: "notDelivered",
    value: false,
  });

  const [showAreYouSure, setShowAreYouSure] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = () => {
    if (!formData.containerNumber) {
      setError("invalidContainerNumber");
    } else {
      setInputs((prev) =>
        Array.isArray(prev)
          ? [...prev, { ...formData, delivered }]
          : [{ ...formData, delivered }]
      );
      onClose();
      setError("");
    }
  };

  const [loadingModal, setLoadingModal] = useState(false);

  const handleDateChange = (e) => {
    const { name, value } = e.target;

    // Update `formData` instead of trying to modify `inputs` directly.
    setFormData((prev) => ({ ...prev, [name]: new Date(value) }));
  };

  const handleAttemptCancel = () => {
    setShowAreYouSure(true);
  };

  const handleCancel = () => {
    setShowAddShippedContainerModal(false);
    setError("");
    // setInputs([]);
  };

  const handleChangeOption = (option) => {
    setDelivered(option);
  };

  return (
    <>
      <Modal
        title="addContainer"
        onClose={handleAttemptCancel}
        footer={
          <>
            {loadingModal ? <Loader /> : null}

            <section className={classes.actions}>
              <CustomButton
                onClick={handleSave}
                title={t("confirm")}
                icon="save"
                iconSize={16}
                disabled={loadingModal}
              />
              <CustomButton
                title={t("cancel")}
                onClick={handleAttemptCancel}
                variant="blank"
                icon="close"
                iconSize={14}
              />
            </section>
          </>
        }
      >
        <div
          className={`${classes.flexableForm} ${classes.flexableFormWithPadding}`}
        >
          {error === "invalidContainerNumber" && (
            <ErrorBox>{t(error)}</ErrorBox>
          )}

          <div className={classes.inputContainer}>
            <span>{t("containerNumber")}: </span>
            <div className={classes.invoiceNumberContainer}>
              <CustomInput
                name="containerNumber"
                value={formData?.containerNumber}
                onChange={(e) => handleInputChange(e)}
                flex={true}
              />
            </div>
          </div>
          <div
            className={`o-row o-row--horizontal-align_space-between o-row--gap-size_400 o-row--vertical-align_center u-width_full-width`}
          >
            <div className={classes.inputContainer}>
              <span>{t("containerPrice")}: </span>
              <CustomInput
                name="amountForSupplier"
                value={formData?.amountForSupplier}
                onChange={(e) => handleInputChange(e)}
                type="moneyV2"
                flex={true}
              />
            </div>
            <div>{selectedSupplier?.currency}</div>
          </div>
          <div className={classes.inputContainer}>
            <span>{t("expectedArrivalDate")}: </span>
            <CustomInput
              name="expectedArrivalDate"
              type="date"
              flex={true}
              value={formatDate(formData?.expectedArrivalDate)}
              onChange={(e) => handleDateChange(e)}
            />
          </div>
          <div className={classes.inputContainer}>
            <span>{t("finalDestination")}: </span>
            <CustomInput
              name="finalDestination"
              value={formData?.finalDestination}
              onChange={(e) => handleInputChange(e)}
              flex={true}
            />
          </div>

          <div className={classes.inputContainer}>
            <span>{t("material")}: </span>
            <CustomInput
              name="material"
              value={formData?.material}
              onChange={(e) => handleInputChange(e)}
              flex={true}
            />
          </div>
          <div className={classes.inputContainer}>
            <span>{t("numberOfBoxes")}: </span>
            <CustomInput
              name="numberOfBoxes"
              value={formData?.numberOfBoxes || ""}
              onChange={(e) => handleInputChange(e)}
              type="moneyV2"
              flex={true}
            />
          </div>
          <div className={classes.inputContainer}>
            <span>{t("measuringUnit")}: </span>
            <CustomInput
              name="measuringUnit"
              value={formData?.measuringUnit || ""}
              onChange={(e) => handleInputChange(e)}
              flex={true}
            />
          </div>
          <div
            className={`o-row o-row--horizontal-align_space-between o-row--gap-size_400 o-row--vertical-align_center u-width_full-width`}
          >
            <div className={classes.inputContainer}>
              <span>{t("unitPrice")}: </span>
              <CustomInput
                name="unitPrice"
                value={formData?.unitPrice || ""}
                onChange={(e) => handleInputChange(e)}
                type="moneyV2"
                flex={true}
              />
            </div>
            <div>{selectedSupplier?.currency}</div>
          </div>
          <div className={classes.inputContainer}>
            <span>{t("shipper")}: </span>
            <CustomInput
              name="shipper"
              value={formData?.shipper}
              onChange={(e) => handleInputChange(e)}
              flex={true}
            />
          </div>
          <div className={classes.inputContainer}>
            <span>{t("dateOfEntry")}: </span>
            <CustomInput
              name="dateOfEntry"
              type="date"
              flex={true}
              value={
                formData?.dateOfEntry
                  ? formData?.dateOfEntry.toISOString().split("T")[0]
                  : ""
              }
              onChange={(e) => handleDateChange(e)}
            />
          </div>

          <div className={classes.inputContainer}>
            <span>{t("shippingDate")}: </span>
            <CustomInput
              name="shippingDate"
              type="date"
              flex={true}
              value={formatDate(formData?.shippingDate)}
              onChange={(e) => handleDateChange(e)}
            />
          </div>

          <div className={classes.inputContainer}>
            <span>{t("exitDate")}: </span>
            <CustomInput
              name="exitDate"
              type="date"
              flex={true}
              value={formatDate(formData?.exitDate)}
              onChange={(e) => handleDateChange(e)}
            />
          </div>
          {/* <div
          className={`o-row o-row--horizontal-align_space-between o-row--gap-size_400 o-row--vertical-align_center u-width_full-width`}
        >
          <div className={classes.inputContainer}>
            <span>{t("abstractAmountForCustomer")}: </span>
            <CustomInput
              name="abstractAmountForCustomer"
              value={formData?.abstractAmountForCustomer}
              onChange={(e) => handleInputChange(e)}
              onKeyDown={(e) => onlyNums(e)}
              type="money"
              flex={true}
            />
          </div>
          <div>{containerCustomer?.currency}</div>
        </div> */}

          <div className={classes.inputContainer}>
            <span>{t("permitsReceivingDate")}: </span>
            <CustomInput
              name="permitsReceivingDate"
              type="date"
              flex={true}
              value={formatDate(formData?.permitsReceivingDate)}
              onChange={(e) => handleDateChange(e)}
            />
          </div>

          <div className={classes.inputContainer}>
            <span>{t("deliveryStatus")}: </span>
            <Dropdown
              options={[
                { title: "delivered", value: true },
                { title: "notDelivered", value: false },
              ]}
              selected={delivered}
              setSelected={(option) => handleChangeOption(option)}
              flex
            />
          </div>

          <div className={classes.inputContainer}>
            <span className={classes.notesClass}>{t("notes")}: </span>
            <CustomInput
              name="notes"
              type="textarea"
              value={formData?.notes || ""}
              onChange={(e) => handleInputChange(e)}
              flex={true}
            />
          </div>
          {/* <PDFUploader
            ref={pdfUploaderRef}
            invoiceNumber={data?.invoiceNumber}
            invoiceDocs={invoiceDocs}
            setInvoiceDocs={setInvoiceDocs}
            mode="upload"
            documents={data?.documents}
            tempDocs={invoiceDocs}
            setTempDocs={setInvoiceDocs}
        /> */}
        </div>
      </Modal>
      {showAreYouSure ? (
        <AreYouSureModal
          shouldCloseModal={shouldCloseModal({
            ...formData,

            // Expect the below from Check

            dateOfEntry: "",
          })}
          onClose={() => setShowAreYouSure(false)}
          onConfirm={handleCancel}
          confirmIconType="whiteCheck"

        />
      ) : null}
    </>
  );
};

export default AddNoCustomerContainerModal;
